/* Public Open Play registration — mirrors the tournament registration flow.
   Payment methods: PayMongo online (full / 50% deposit), GCash QR + proof, Pay at venue. */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Copy, ExternalLink, Upload, Calendar, Clock, Users, Plus, X, Check, CreditCard, QrCode, Store } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { fmtDate, fmtDateShort } from '../utils/dates';
import { fmt12 } from '../utils/sportsUtils';
import SkillInput from '../components/SkillInput';
import { divisionFit, fittingDivision, fmtDuprRange, hasRange } from '../utils/skillLevels';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND;
const RED = '#E63946'; // legacy — PayMongo return screens only
// Boarding-pass palette (Round 1) — the register form is the top half of the same ticket.
const G1 = '#FF1A3E';
const G2 = '#FF6B35';
const GRAD = `linear-gradient(135deg, ${G1}, ${G2})`;
const EYEBROW = '#FF3B55';
const GREEN = '#2EE07C';
const GOLD = '#FFC24B';
const PURPLE = '#A78BFA';

const inputStyle = {
  width: '100%', background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 8,
  padding: '12px', color: '#fff', fontSize: 14, outline: 'none',
};

function AmountPill({ active, onClick, testid, children }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick}
      className="px-4 rounded-full text-xs font-bold"
      style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center', background: active ? G1 : '#1A1A1A', color: active ? '#fff' : '#A6A69E', border: `1px solid ${active ? G1 : '#2A2A2A'}`, cursor: 'pointer' }}>
      {children}
    </button>
  );
}

function MetaChip({ icon: Icon, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#C9C9C2', background: '#1E1E1E', border: '1px solid #333', borderRadius: 999, padding: '4px 10px', whiteSpace: 'nowrap' }}>
      <Icon size={13} style={{ color: G2, flexShrink: 0 }} />{children}
    </span>
  );
}

function PayCard({ active, icon: Icon, title, desc, onClick, testid, children }) {
  return (
    <div style={{ background: active ? 'rgba(255,26,62,0.08)' : '#0F0F0F', border: `1px solid ${active ? G1 : '#2A2A2A'}`, borderRadius: 12, boxShadow: active ? '0 0 18px rgba(255,26,62,0.14)' : 'none', transition: 'all .15s' }}>
      <button type="button" data-testid={testid} onClick={onClick}
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 12px', background: 'transparent', border: 'none', cursor: 'pointer' }}>
        <span style={{ width: 36, height: 36, borderRadius: 9, background: '#1E1E1E', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={19} style={{ color: G2 }} />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <p className="text-sm font-bold" style={{ margin: 0, color: active ? '#fff' : '#E8E8E2' }}>{title}</p>
          <p style={{ margin: '2px 0 0', fontSize: 10.5, color: active ? '#A6A69E' : '#8A8A82' }}>{desc}</p>
        </span>
        <span style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, background: active ? '#fff' : 'transparent', border: active ? `5px solid ${G1}` : '1.5px solid #3A3A3A', boxSizing: 'border-box' }} />
      </button>
      {children && <div style={{ padding: '0 12px 12px 58px' }}>{children}</div>}
    </div>
  );
}

export default function SportsOpenPlayRegisterPage() {
  const { slug, sid } = useParams();
  const [params] = useSearchParams();
  const returnPlayer = params.get('player');
  const returnPay = params.get('pay');

  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', gender: '', division_id: '', org_label: '' });
  // Starting level: the 1-10 bar or a typed DUPR — every session (plan 7.2)
  const [skill, setSkill] = useState({ self_skill: 5, dupr: '' });
  const [payMethod, setPayMethod] = useState('venue');
  const [amountChoice, setAmountChoice] = useState('full');
  const [proofKey, setProofKey] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avatarKey, setAvatarKey] = useState(null);
  const [avatarThumbKey, setAvatarThumbKey] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [done, setDone] = useState(null); // registered player
  const [payState, setPayState] = useState(returnPay === 'success' ? 'checking' : returnPay === 'cancel' ? 'cancelled' : null);
  const [payToken, setPayToken] = useState(null);

  // Team registration mode — one contact registers several players in one go.
  const navigate = useNavigate();
  const [mode, setMode] = useState('solo');
  const [teamContact, setTeamContact] = useState({ name: '', phone: '', email: '', org_label: '' });
  const [teamMembers, setTeamMembers] = useState([
    { name: '', division_id: '', self_skill: 5, dupr: '' },
    { name: '', division_id: '', self_skill: 5, dupr: '' },
    { name: '', division_id: '', self_skill: 5, dupr: '' },
  ]);
  const [teamPayMethod, setTeamPayMethod] = useState('venue');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sports/public/${slug}/open-play/${sid}`);
      setSession(r.data);
      const divs = r.data.divisions || [];
      if (divs.length === 1) setForm(f => ({ ...f, division_id: divs[0].id }));
      const pays = r.data.payments || {};
      const fee = parseFloat(r.data.entry_fee || 0);
      if (fee > 0) {
        if (pays.enabled && pays.provider === 'paymongo') { setPayMethod('paymongo'); setTeamPayMethod('paymongo'); }
        else if (pays.gcash_qr_url) { setPayMethod('gcash'); setTeamPayMethod('gcash'); }
        else { setPayMethod('venue'); setTeamPayMethod('venue'); }
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Session not found');
    }
  }, [slug, sid]);

  useEffect(() => { load(); }, [load]);

  // Live capacity: refresh counts while the form is open (session only — never
  // touches form/payment state, so a mid-typing refresh can't clobber choices).
  useEffect(() => {
    if (done || payState) return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/sports/public/${slug}/open-play/${sid}`);
        setSession(r.data);
      } catch { /* keep last known counts */ }
    }, 30000);
    return () => clearInterval(t);
  }, [slug, sid, done, payState]);

  // PayMongo return: poll payment status
  useEffect(() => {
    if (returnPay !== 'success' || !returnPlayer) return;
    let tries = 0; let stop = false;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/api/sports/public/${slug}/open-play/${sid}/players/${returnPlayer}/pay/status`);
        if (r.data?.paid) { if (!stop) { setPayState('paid'); setPayToken(r.data.personal_token); } return; }
      } catch { /* retry */ }
      tries += 1;
      if (tries >= 8) { if (!stop) setPayState('pending'); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { stop = true; };
  }, [returnPay, returnPlayer, slug, sid]);

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/api/sports/public/${slug}/open-play/${sid}/upload-proof`, fd);
      setProofKey(r.data.proof_key);
    } catch { setError('Proof upload failed — try again'); }
    setUploading(false);
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/api/sports/public/${slug}/open-play/${sid}/upload-avatar`, fd);
      setAvatarKey(r.data.avatar_key); setAvatarThumbKey(r.data.avatar_thumb_key);
      setAvatarPreview(URL.createObjectURL(file));
    } catch { setError('Avatar upload failed — try a PNG or JPG'); }
    setAvatarUploading(false);
  };

  const submit = async () => {
    setError('');
    if (!form.name.trim()) { setError('Your name is required'); return; }
    if (!form.phone.trim()) { setError('Phone number is required'); return; }
    if ((session.divisions || []).length > 1 && !form.division_id) { setError('Please pick a division'); return; }
    const fee = parseFloat(session.entry_fee || 0);
    if (fee > 0 && payMethod === 'gcash' && !proofKey) { setError('Please upload your GCash payment screenshot'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/sports/public/${slug}/open-play/${sid}/register`, {
        ...form, payment_method: fee > 0 ? payMethod : undefined, proof_key: proofKey,
        avatar_key: avatarKey, avatar_thumb_key: avatarThumbKey,
        amount_choice: fee > 0 && (payMethod === 'paymongo' || payMethod === 'gcash') ? amountChoice : undefined,
        self_skill: skill.self_skill,
        dupr: skill.dupr || undefined,
      });
      const player = r.data;
      if (fee > 0 && payMethod === 'paymongo' && !player.waitlisted) {
        try {
          const c = await axios.post(
            `${API}/api/sports/public/${slug}/open-play/${sid}/players/${player.id}/pay/checkout`,
            { amount_choice: amountChoice });
          window.location.href = c.data.checkout_url;
          return;
        } catch {
          // Registered but checkout couldn't start — show the success screen
          // (resubmitting the form would create a duplicate registration);
          // the fee can be settled at the venue instead.
          setDone(player);
          setSaving(false);
          return;
        }
      }
      setDone(player);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Registration failed');
    }
    setSaving(false);
  };

  const setMember = (i, patch) => setTeamMembers(ms => ms.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const addMember = () => setTeamMembers(ms => [...ms, { name: '', division_id: '', self_skill: 5, dupr: '' }]);
  const removeMember = (i) => setTeamMembers(ms => ms.filter((_, j) => j !== i));

  const submitTeam = async () => {
    setError('');
    const feeV = parseFloat(session.entry_fee || 0);
    const divsV = session.divisions || [];
    if (!teamContact.name.trim()) { setError('Contact name is required'); return; }
    if (!teamContact.phone.trim()) { setError('Contact phone number is required'); return; }
    const named = teamMembers.filter(m => m.name.trim());
    if (!named.length) { setError('Add at least one player'); return; }
    if (divsV.length > 1 && named.some(m => !m.division_id)) { setError('Pick a division for every player'); return; }
    if (feeV > 0 && teamPayMethod === 'gcash' && !proofKey) { setError('Please upload your GCash payment screenshot'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/api/sports/public/${slug}/open-play/${sid}/register-team`, {
        contact_name: teamContact.name.trim(),
        contact_phone: teamContact.phone.trim(),
        contact_email: teamContact.email.trim(),
        org_label: teamContact.org_label.trim(),
        members: named.map(m => ({
          name: m.name.trim(),
          division_id: m.division_id || undefined,
          self_skill: m.self_skill,
          dupr: m.dupr || undefined,
        })),
        payment_method: feeV > 0 ? teamPayMethod : undefined,
        proof_key: proofKey,
      });
      const gid = r.data.group_id;
      // Online checkout: one PayMongo session for the whole team's total.
      // Waitlisted teams skip payment (same rule as solo) — pay when promoted.
      if (feeV > 0 && teamPayMethod === 'paymongo' && !r.data.waitlisted) {
        try {
          const c = await axios.post(`${API}/api/sports/public/${slug}/open-play/${sid}/team/${gid}/pay/checkout`);
          window.location.href = c.data.checkout_url;
          return;
        } catch {
          // Registration succeeded but checkout couldn't start. Land on the
          // team page (NOT back on the form — resubmitting would duplicate
          // the team); fees can be settled at the venue instead.
          navigate(`/book/${slug}/open-play/${sid}/team/${gid}?pay=cancel`);
          return;
        }
      }
      navigate(`/book/${slug}/open-play/${sid}/team/${gid}`);
      return;
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Team registration failed');
    }
    setSaving(false);
  };

  // Frontend route — must be the app origin, NOT the API host (dead on local dev,
  // and the backend would 404 it in any split-host deployment)
  const personalUrl = (token) => `${window.location.origin}/sports/open-play/player/${token}`;
  const copyLink = (token) => { navigator.clipboard.writeText(personalUrl(token)); };

  const page = { minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif', padding: '32px 16px' };
  const card = { maxWidth: 520, margin: '0 auto', background: '#141414', border: '1px solid #262626', borderRadius: 16, padding: 24 };

  // ── PayMongo return screens ──
  if (payState) {
    return (
      <div style={page} data-testid="op-pay-return">
        <div style={{ ...card, textAlign: 'center' }}>
          {payState === 'checking' && (<><Loader2 className="h-10 w-10 mx-auto mb-3 animate-spin" style={{ color: RED }} /><p>Confirming your payment…</p></>)}
          {payState === 'paid' && (
            <>
              <CheckCircle className="h-12 w-12 mx-auto mb-3" style={{ color: '#22C55E' }} />
              <h2 className="text-xl font-black uppercase mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>Payment Confirmed!</h2>
              <p className="text-sm mb-4" style={{ color: '#A3A3A3' }}>You're registered. Use your personal link on game day to check in.</p>
              {payToken && (
                <a data-testid="op-personal-link" href={personalUrl(payToken)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold uppercase"
                  style={{ background: RED, color: '#fff', textDecoration: 'none' }}>
                  <ExternalLink className="h-4 w-4" /> My Player Page
                </a>
              )}
            </>
          )}
          {payState === 'pending' && (<><Clock className="h-12 w-12 mx-auto mb-3" style={{ color: '#F59E0B' }} /><p className="text-sm" style={{ color: '#A3A3A3' }}>Payment is still processing. You're registered — the organizer will confirm your payment shortly.</p></>)}
          {payState === 'cancelled' && (<><XCircle className="h-12 w-12 mx-auto mb-3" style={{ color: '#EF4444' }} /><p className="text-sm" style={{ color: '#A3A3A3' }}>Payment cancelled. You're still registered — you can pay at the venue or try online again from the organizer.</p></>)}
        </div>
      </div>
    );
  }

  if (!session) {
    return <div style={page}><div style={{ ...card, textAlign: 'center' }}>{error ? <p style={{ color: '#EF4444' }}>{error}</p> : <Loader2 className="h-8 w-8 mx-auto animate-spin" style={{ color: RED }} />}</div></div>;
  }

  const fee = parseFloat(session.entry_fee || 0);
  const pays = session.payments || {};
  const canOnline = fee > 0 && pays.enabled && pays.provider === 'paymongo';
  const canDeposit = canOnline && (pays.options || []).includes('deposit50');
  const canVenue = fee === 0 || (pays.options || []).includes('counter') || (!pays.enabled && !pays.gcash_qr_url);
  const divs = session.divisions || [];
  const regClosed = session.registration_is_open != null ? !session.registration_is_open
    : !['registration_open', 'active'].includes(session.status);
  const teamCount = teamMembers.filter(m => m.name.trim()).length;

  // Hero state — capacity only when EVERY division carries a hard cap (same
  // honesty rule as the events-list slots_left); otherwise show the live count.
  const isLive = session.status === 'active';
  const caps = divs.map(d => d.hard_cap);
  const capTotal = caps.length && caps.every(c => c > 0) ? caps.reduce((a, b) => a + b, 0) : null;
  const playersIn = session.player_count || 0;
  const slotsLeft = capTotal != null ? Math.max(0, capTotal - playersIn) : null;
  const isFull = capTotal != null && slotsLeft === 0;
  const selDiv = divs.find(d => d.id === form.division_id);
  const selDivFull = !!(selDiv?.hard_cap && (selDiv.player_count || 0) >= selDiv.hard_cap);
  const joinsWaitlist = isFull || selDivFull;
  const chip = regClosed ? { c: '#A6A69E', label: 'Registration closed', pulse: false }
    : isFull ? { c: PURPLE, label: 'Session full', pulse: false }
    : isLive ? { c: GOLD, label: 'Live now', pulse: true }
    : { c: GREEN, label: 'Registration open', pulse: false };

  // ── Success screen — a boarding pass, not a toast (UI checklist 1.2) ──
  if (done) {
    const wl = !!done.waitlisted;
    const stColor = wl ? '#A78BFA' : '#2EE07C';
    const link = personalUrl(done.personal_token);
    const feeLine = fee <= 0 ? 'Free entry'
      : payMethod === 'gcash' ? `₱${fee} · GCash proof under review`
      : payMethod === 'paymongo' ? `₱${fee} · paid online`
      : `₱${fee} · pay at the venue`;
    const calUrl = session.date ? (() => {
      const d = session.date.replace(/-/g, '');
      const ts = (session.time_start || '08:00').replace(':', '') + '00';
      const te = (session.time_end || '22:00').replace(':', '') + '00';
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(session.name)}&dates=${d}T${ts}/${d}T${te}&details=${encodeURIComponent('Your player pass: ' + link)}`;
    })() : null;
    const savePass = async () => {
      copyLink(done.personal_token);
      if (navigator.share) {
        try { await navigator.share({ title: session.name, text: 'My player pass', url: link }); } catch { /* user closed share sheet */ }
      }
    };
    return (
      <div style={page} data-testid="op-register-success">
        <div style={{ maxWidth: 460, margin: '0 auto', background: '#141414', border: '1px solid #2E2E2E', borderRadius: 16, overflow: 'hidden', boxShadow: `0 0 34px ${stColor}1A` }}>
          <div style={{ padding: '18px 20px', display: 'flex', gap: 16, alignItems: 'center', borderBottom: '1px dashed #3A3A3A', background: 'linear-gradient(180deg, #1A1A1A, #141414)' }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: 8, flexShrink: 0, boxShadow: '0 0 22px rgba(255,255,255,0.12)' }}>
              <QRCodeSVG value={link} size={72} data-testid="op-pass-qr" />
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: stColor, fontWeight: 700, textShadow: `0 0 10px ${stColor}55` }}>
                {wl ? 'On the waitlist' : '✓ You\'re in'}
              </p>
              <p style={{ margin: '3px 0 0', fontSize: 17, fontWeight: 700, color: '#fff', fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 0.5 }}>{done.name || form.name}</p>
              <p style={{ margin: '3px 0 0', fontSize: 12, color: '#A6A69E' }}>{session.name}{session.date ? ` · ${fmtDate(session.date)}` : ''}</p>
              <p style={{ margin: '5px 0 0', fontSize: 11.5, color: '#FFC24B', fontWeight: 600 }}>{feeLine}</p>
            </div>
          </div>
          <div style={{ padding: '14px 20px 18px' }}>
            {wl && (
              <p style={{ fontSize: 12, color: '#A6A69E', lineHeight: 1.6, margin: '0 0 8px' }}>
                The session is full right now. If a spot opens you're promoted automatically — this pass is your place in line.
              </p>
            )}
            {[
              'Save this pass — it\'s your personal QR for game day',
              'Arrive early and scan it at the desk to check in',
              'Watch the queue on your pass — your match and court show live',
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0' }}>
                <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#FFC24B26', color: '#FFC24B', fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</span>
                <span style={{ fontSize: 12.5, color: '#A6A69E', lineHeight: 1.5 }}>{s}</span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button data-testid="op-copy-personal" onClick={savePass}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 999, padding: '11px 20px', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer', border: 'none', color: '#fff', background: 'linear-gradient(135deg, #FF1A3E, #FF6B35)', boxShadow: '0 0 24px rgba(255,26,62,0.4)', fontFamily: 'Oswald, sans-serif' }}>
                <Copy className="h-4 w-4" /> Save my pass
              </button>
              {calUrl && (
                <a data-testid="op-add-calendar" href={calUrl} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 999, padding: '11px 20px', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', cursor: 'pointer', border: '1px solid rgba(255,26,62,0.4)', color: '#fff', background: 'rgba(255,26,62,0.10)', textDecoration: 'none', fontFamily: 'Oswald, sans-serif' }}>
                  Add to calendar
                </a>
              )}
              <a data-testid="op-personal-link" href={link}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, borderRadius: 999, padding: '11px 20px', fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', border: '1px solid #333', color: '#fff', background: '#1E1E1E', textDecoration: 'none', fontFamily: 'Oswald, sans-serif' }}>
                <ExternalLink className="h-4 w-4" /> Open pass
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={page} data-testid="op-register-page">
      <div style={{ ...card, padding: 0, overflow: 'hidden', border: '1px solid #2E2E2E', boxShadow: `0 0 34px ${chip.c}1A` }}>
        {/* ── Hero — the top half of the Round-1 boarding pass (checklist 1.2) ── */}
        <div style={{ padding: '18px 20px 16px', background: 'linear-gradient(180deg, #1A1A1A, #141414)', borderBottom: '1px dashed #3A3A3A' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {session.facility?.logo_url && (
                <img src={session.facility.logo_url} alt="" data-testid="op-hero-logo"
                  style={{ width: 30, height: 30, borderRadius: 8, objectFit: 'cover', border: '1px solid #333', flexShrink: 0 }} />
              )}
              <p style={{ margin: 0, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: EYEBROW, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                Open Play · {session.facility?.name || slug}
              </p>
            </div>
            <span data-testid="op-hero-status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 700, color: chip.c, background: `${chip.c}1F`, border: `1px solid ${chip.c}73`, borderRadius: 999, padding: '4px 10px', textShadow: `0 0 10px ${chip.c}80`, whiteSpace: 'nowrap', flexShrink: 0 }}>
              <span className={chip.pulse ? 'animate-pulse' : ''} style={{ width: 6, height: 6, borderRadius: '50%', background: chip.c, boxShadow: `0 0 8px ${chip.c}` }} />
              {chip.label}
            </span>
          </div>
          <h1 data-testid="op-hero-name" style={{ margin: '10px 0 0', fontFamily: 'Oswald, sans-serif', fontSize: 24, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.12, background: GRAD, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            {session.name}
          </h1>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            <MetaChip icon={Calendar}>{fmtDateShort(session.date)}</MetaChip>
            <MetaChip icon={Clock}>{fmt12(session.time_start)} – {fmt12(session.time_end)}</MetaChip>
            <MetaChip icon={Users}>{session.game_mode === 'singles' ? 'Singles' : 'Doubles'} rotation</MetaChip>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid #262626' }}>
            <div>
              <p data-testid="op-hero-fee" style={{ margin: 0, fontFamily: 'Oswald, sans-serif', fontSize: 26, fontWeight: 700, color: regClosed ? '#E8E8E2' : chip.c, lineHeight: 1 }}>
                {fee > 0 ? `₱${fee.toLocaleString()}` : 'FREE'}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 10.5, color: '#8A8A82' }}>{fee > 0 ? 'per player · pay your way' : 'free entry — just show up'}</p>
            </div>
            <div data-testid="op-hero-capacity" style={{ textAlign: 'right', minWidth: 130 }}>
              {capTotal != null ? (
                <>
                  <p style={{ margin: '0 0 5px', fontSize: 12, fontWeight: 700, color: regClosed ? '#A6A69E' : chip.c }}>
                    {slotsLeft} slot{slotsLeft === 1 ? '' : 's'} left
                  </p>
                  <div style={{ height: 5, borderRadius: 999, background: '#262626', overflow: 'hidden' }}>
                    <div style={{ width: `${Math.min(100, Math.round((playersIn / capTotal) * 100))}%`, height: '100%', borderRadius: 999, background: chip.c, transition: 'width .6s' }} />
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 10, color: '#8A8A82' }}>{playersIn} of {capTotal} players in</p>
                </>
              ) : (
                <>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#E8E8E2', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Users size={13} style={{ color: regClosed ? '#A6A69E' : chip.c }} /> {playersIn} player{playersIn === 1 ? '' : 's'} in
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: 10, color: '#8A8A82' }}>{isLive ? 'session is live' : 'no player cap'}</p>
                </>
              )}
            </div>
          </div>
          {isLive && !regClosed && !isFull && (
            <p style={{ margin: '10px 0 0', fontSize: 11, color: GOLD }}>Session is live — register now and you're paired into the next set.</p>
          )}
          {isFull && !regClosed && (
            <p style={{ margin: '10px 0 0', fontSize: 11, color: PURPLE }}>Join the waitlist — you're promoted automatically when a spot opens.</p>
          )}
        </div>

        <div style={{ padding: '16px 20px 22px' }}>
        {regClosed ? (
          <p className="text-sm p-4 rounded-lg" style={{ background: '#1A1A1A', color: '#F59E0B' }}>Registration is closed for this session.</p>
        ) : (
          <>
            {/* Solo / Team segmented toggle — quiet, so the register CTA stays the lone primary */}
            <div className="flex mb-4" style={{ background: '#0F0F0F', border: '1px solid #2A2A2A', borderRadius: 999, padding: 3 }}>
              <button type="button" data-testid="op-mode-solo" onClick={() => setMode('solo')}
                className="flex-1 text-xs font-black uppercase tracking-wider"
                style={{ padding: '13px 0', borderRadius: 999, background: mode === 'solo' ? 'rgba(255,26,62,0.16)' : 'transparent', color: mode === 'solo' ? '#fff' : '#8A8A82', border: `1px solid ${mode === 'solo' ? 'rgba(255,26,62,0.55)' : 'transparent'}`, fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>
                Just me
              </button>
              <button type="button" data-testid="op-mode-team" onClick={() => setMode('team')}
                className="flex-1 text-xs font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5"
                style={{ padding: '13px 0', borderRadius: 999, background: mode === 'team' ? 'rgba(255,26,62,0.16)' : 'transparent', color: mode === 'team' ? '#fff' : '#8A8A82', border: `1px solid ${mode === 'team' ? 'rgba(255,26,62,0.55)' : 'transparent'}`, fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>
                <Users className="h-3.5 w-3.5" /> Register a team
              </button>
            </div>

            {mode === 'team' ? (
              <div data-testid="op-team-form">
                <p className="text-xs font-bold uppercase mb-2" style={{ color: '#A3A3A3' }}>Contact person</p>
                <div className="space-y-3">
                  <input data-testid="op-team-contact-name" style={inputStyle} placeholder="Contact name *"
                    value={teamContact.name} onChange={e => setTeamContact({ ...teamContact, name: e.target.value })} />
                  <input data-testid="op-team-contact-phone" style={inputStyle} placeholder="Contact phone *"
                    value={teamContact.phone} onChange={e => setTeamContact({ ...teamContact, phone: e.target.value })} />
                  <div>
                    <input data-testid="op-team-contact-email" style={inputStyle} placeholder="Contact email (recommended)"
                      value={teamContact.email} onChange={e => setTeamContact({ ...teamContact, email: e.target.value })} />
                    <p className="text-[11px] mt-1.5 mb-2" style={{ color: '#666' }}>
                      The whole team&apos;s QR codes are sent to this email as one link.
                    </p>
                  </div>
                  <input data-testid="op-team-org" style={inputStyle} placeholder="Team / club name (optional)"
                    value={teamContact.org_label} onChange={e => setTeamContact({ ...teamContact, org_label: e.target.value })} />
                </div>

                <p className="text-xs font-bold uppercase mt-5 mb-2" style={{ color: '#A3A3A3' }}>Players</p>
                <p className="text-[11px] -mt-1 mb-3" style={{ color: '#666' }}>
                  Rate each player&apos;s skill below their name (or type their DUPR). It&apos;s only their
                  <b> starting point</b> — matchmaking learns from real results after that.
                </p>
                <div className="space-y-5">
                  {teamMembers.map((m, i) => (
                    <div key={i}>
                      <div className="flex gap-2 items-center">
                        <input data-testid={`op-team-member-name-${i}`} style={{ ...inputStyle, flex: 1 }}
                          placeholder={`Player ${i + 1} name${i === 0 ? ' *' : ''}`}
                          value={m.name} onChange={e => setMember(i, { name: e.target.value })} />
                        {divs.length > 1 && (
                          <select data-testid={`op-team-member-div-${i}`} style={{ ...inputStyle, width: 120, flexShrink: 0 }}
                            value={m.division_id} onChange={e => setMember(i, { division_id: e.target.value })}>
                            <option value="">Division *</option>
                            {divs.map(d => <option key={d.id} value={d.id}>{d.name}{hasRange(d) ? ` (${d.min_level}–${d.max_level})` : ''}</option>)}
                          </select>
                        )}
                        {teamMembers.length > 1 && (
                          <button type="button" data-testid={`op-team-member-remove-${i}`} onClick={() => removeMember(i)}
                            className="p-2 rounded-lg flex-shrink-0" style={{ background: '#1A1A1A', border: '1px solid #2A2A2A' }}>
                            <X className="h-4 w-4" style={{ color: '#737373' }} />
                          </button>
                        )}
                      </div>
                      {/* Indented + left rail: reads as "this bar belongs to the
                          name above", not as a stand-alone full-width block. */}
                      <div style={{ marginLeft: 16, paddingLeft: 14, borderLeft: '2px solid #333' }}>
                        <SkillInput value={m} onChange={v => setMember(i, v)}
                          sliderId={`op-team-skill-${i}`} testid={`op-team-skill-${i}`} />
                        <DivisionAdvice divs={divs} divisionId={m.division_id} level={m.self_skill}
                          testid={`op-team-advice-${i}`}
                          onSwitch={id => setMember(i, { division_id: id })} />
                      </div>
                    </div>
                  ))}
                </div>
                <button type="button" data-testid="op-team-add-member" onClick={addMember}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                  style={{ background: '#1A1A1A', color: '#A3A3A3', border: '1px dashed #333' }}>
                  <Plus className="h-3.5 w-3.5" /> Add player
                </button>

                {fee > 0 && (
                  <div className="mt-5">
                    <p className="text-[10px] font-bold uppercase mb-2" style={{ color: '#A6A69E', letterSpacing: 2 }}>
                      How you'll pay · ₱{fee.toLocaleString()} × {teamCount} = ₱{(fee * teamCount).toLocaleString()}
                    </p>
                    <div className="space-y-2">
                      {canOnline && (
                        <PayCard testid="op-team-pay-online" active={teamPayMethod === 'paymongo'} onClick={() => setTeamPayMethod('paymongo')}
                          icon={CreditCard} title="Pay online"
                          desc={`One secure checkout for the whole team${pays.fee_percent > 0 ? ` · +${pays.fee_percent}% convenience fee` : ''}`} />
                      )}
                      {pays.gcash_qr_url && (
                        <PayCard testid="op-team-pay-gcash" active={teamPayMethod === 'gcash'} onClick={() => setTeamPayMethod('gcash')}
                          icon={QrCode} title={`GCash${pays.gcash_name ? ` — ${pays.gcash_name}` : ''}`}
                          desc="Send the TOTAL for the whole team, then upload one screenshot">
                          {teamPayMethod === 'gcash' && (
                            <div className="space-y-2">
                              <img src={pays.gcash_qr_url} alt="GCash QR" style={{ width: 150, height: 150, objectFit: 'contain', background: '#fff', borderRadius: 8, padding: 6 }} />
                              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer"
                                style={{ background: proofKey ? 'rgba(34,197,94,0.12)' : '#1A1A1A', color: proofKey ? '#22C55E' : '#A3A3A3', border: '1px solid #2A2A2A' }}>
                                <Upload className="h-4 w-4" />
                                {uploading ? 'Uploading…' : proofKey ? '✓ Proof uploaded — change' : 'Upload payment screenshot *'}
                                <input data-testid="op-team-proof-upload" type="file" accept="image/*" className="hidden"
                                  onChange={e => uploadProof(e.target.files?.[0])} />
                              </label>
                            </div>
                          )}
                        </PayCard>
                      )}
                      {canVenue && (
                        <PayCard testid="op-team-pay-venue" active={teamPayMethod === 'venue'} onClick={() => setTeamPayMethod('venue')}
                          icon={Store} title="Pay at the venue" desc="Settle all entry fees at the court" />
                      )}
                    </div>
                    {canOnline && teamPayMethod === 'paymongo' && (
                      <p className="text-[11px] mt-2" style={{ color: '#737373' }}>
                        Teams pay the full amount in one checkout — the 50% deposit option is for individual signups only.
                      </p>
                    )}
                  </div>
                )}

                {error && <p className="text-xs mt-3" style={{ color: '#EF4444' }}>{error}</p>}
                <button data-testid="op-team-submit" onClick={submitTeam} disabled={saving || uploading}
                  className="mt-5 w-full py-3.5 text-sm font-black uppercase"
                  style={{ background: GRAD, color: '#fff', border: 'none', borderRadius: 999, boxShadow: '0 0 24px rgba(255,26,62,0.4)', letterSpacing: 1.5, opacity: (saving || uploading) ? 0.6 : 1, fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>
                  {saving ? 'Registering team…'
                    : isFull ? 'Join the waitlist'
                    : teamCount > 0
                      ? `Register ${teamCount} player${teamCount === 1 ? '' : 's'}${fee > 0 && teamPayMethod === 'paymongo' ? ` & pay ₱${(fee * teamCount).toLocaleString()} online` : ''}`
                      : 'Register team'}
                </button>
              </div>
            ) : (
            <>
            <div className="space-y-3">
              <input data-testid="op-reg-name" style={inputStyle} placeholder="Your name *"
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <input data-testid="op-reg-phone" style={inputStyle} placeholder="Phone number *"
                value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              <div>
                <input data-testid="op-reg-email" style={inputStyle} placeholder="Active email (recommended)"
                  value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                <p className="text-[11px] mt-1.5 mb-2" style={{ color: '#666' }}>
                  Use an email you actively check — needed for self check-in, pause requests, and live queue updates on game day.
                </p>
              </div>
              <div data-testid="op-reg-gender">
                <p className="text-[10px] font-bold uppercase mb-2 mt-1" style={{ color: '#A6A69E', letterSpacing: 2 }}>Gender (optional)</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[['M', '♂ Male', '#3B82F6'], ['F', '♀ Female', '#EC4899'], ['', 'Prefer not to say', '#8A8A82']].map(([val, label, color]) => (
                    <button key={label} type="button" data-testid={`op-reg-gender-${val || 'skip'}`}
                      onClick={() => setForm({ ...form, gender: val })}
                      style={{ flex: 1, padding: '9px 6px', borderRadius: 10, fontSize: 12, fontWeight: 700,
                        cursor: 'pointer', textTransform: 'uppercase', fontFamily: 'Oswald, sans-serif',
                        ...(form.gender === val
                          ? { background: `${color}22`, color, border: `1px solid ${color}66` }
                          : { background: '#0F0F0F', color: '#8A8A82', border: '1px solid #2A2A2A' }) }}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <input data-testid="op-reg-org" style={inputStyle} placeholder="Representing (org / club — optional)"
                value={form.org_label} onChange={e => setForm({ ...form, org_label: e.target.value })} />
              <div className="mt-1" data-testid="op-skill-block">
                <SkillInput value={skill} onChange={setSkill} testid="op-reg-skill" />
              </div>
              {divs.length > 1 && (
                <div data-testid="op-reg-division">
                  <p className="text-[10px] font-bold uppercase mb-2 mt-1" style={{ color: '#A6A69E', letterSpacing: 2 }}>Your division</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {divs.map(d => {
                      const full = d.hard_cap && (d.player_count || 0) >= d.hard_cap;
                      const active = form.division_id === d.id;
                      const left = d.hard_cap ? Math.max(0, d.hard_cap - (d.player_count || 0)) : null;
                      return (
                        <button type="button" key={d.id} data-testid={`op-reg-division-${d.id}`}
                          onClick={() => setForm({ ...form, division_id: d.id })}
                          style={{ position: 'relative', textAlign: 'left', background: active ? 'rgba(255,26,62,0.10)' : '#0F0F0F', border: `1px solid ${active ? G1 : '#2A2A2A'}`, borderRadius: 10, padding: '10px 12px', boxShadow: active ? '0 0 16px rgba(255,26,62,0.12)' : 'none', cursor: 'pointer', opacity: full && !active ? 0.85 : 1 }}>
                          {active && (
                            <span style={{ position: 'absolute', top: 8, right: 8, width: 16, height: 16, borderRadius: '50%', background: G1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Check size={11} style={{ color: '#fff' }} />
                            </span>
                          )}
                          <p style={{ margin: 0, fontFamily: 'Oswald, sans-serif', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', color: active ? '#fff' : '#A6A69E', paddingRight: active ? 20 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</p>
                          <p style={{ margin: '3px 0 0', fontSize: 10.5, color: full ? PURPLE : active ? '#C9C9C2' : '#8A8A82', fontWeight: full ? 700 : 400 }}>
                            {full ? 'Full — waitlist open' : `${d.player_count || 0} in${left != null ? ` · ${left} slot${left === 1 ? '' : 's'} left` : ''}`}
                          </p>
                          {hasRange(d) && (
                            <p data-testid={`op-reg-division-range-${d.id}`} style={{ margin: '3px 0 0', fontSize: 10.5, color: divisionFit(d, skill.self_skill) === 'ok' ? '#22C55E' : '#8A8A82' }}>
                              Level {d.min_level}–{d.max_level} · {fmtDuprRange(d.min_level, d.max_level)}
                              {divisionFit(d, skill.self_skill) === 'ok' ? ' · fits you' : ''}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <DivisionAdvice divs={divs} divisionId={form.division_id} level={skill.self_skill}
                    testid="op-reg-advice" onSwitch={id => setForm({ ...form, division_id: id })} />
                </div>
              )}

              {/* Avatar upload */}
              <div className="flex items-center gap-3 pt-1">
                <div style={{ width: 52, height: 52, borderRadius: 26, border: '1px solid #2A2A2A', overflow: 'hidden', flexShrink: 0, background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {avatarPreview ? <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span style={{ fontSize: 10, color: '#525252' }}>Photo</span>}
                </div>
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer"
                    style={{ background: avatarThumbKey ? 'rgba(34,197,94,0.12)' : '#1A1A1A', color: avatarThumbKey ? '#22C55E' : '#A3A3A3', border: '1px solid #2A2A2A' }}>
                    <Upload className="h-3.5 w-3.5" />
                    {avatarUploading ? 'Uploading…' : avatarThumbKey ? '✓ Avatar attached — change' : 'Upload avatar (optional)'}
                    <input data-testid="op-reg-avatar" type="file" accept="image/*" className="hidden"
                      onChange={e => uploadAvatar(e.target.files?.[0])} />
                  </label>
                  <p className="text-[11px] mt-1" style={{ color: '#555' }}>Your face OR your organization&apos;s logo.</p>
                </div>
              </div>
            </div>


            {fee > 0 && (
              <div className="mt-5">
                <p className="text-[10px] font-bold uppercase mb-2" style={{ color: '#A6A69E', letterSpacing: 2 }}>How you'll pay · ₱{fee.toLocaleString()}</p>
                <div className="space-y-2">
                  {canOnline && (
                    <PayCard testid="op-pay-online" active={payMethod === 'paymongo'} onClick={() => setPayMethod('paymongo')}
                      icon={CreditCard} title="Pay online"
                      desc={`GCash · QR Ph · card — secure checkout${pays.fee_percent > 0 ? ` · +${pays.fee_percent}% convenience fee` : ''}`}>
                      {payMethod === 'paymongo' && canDeposit && (
                        <div className="flex gap-2 flex-wrap">
                          <AmountPill testid="op-pay-full" active={amountChoice === 'full'} onClick={() => setAmountChoice('full')}>Pay 100% · ₱{fee}</AmountPill>
                          <AmountPill testid="op-pay-deposit" active={amountChoice === 'deposit50'} onClick={() => setAmountChoice('deposit50')}>50% deposit · ₱{Math.round(fee / 2)}</AmountPill>
                        </div>
                      )}
                    </PayCard>
                  )}
                  {pays.gcash_qr_url && (
                    <PayCard testid="op-pay-gcash" active={payMethod === 'gcash'} onClick={() => setPayMethod('gcash')}
                      icon={QrCode} title={`GCash${pays.gcash_name ? ` — ${pays.gcash_name}` : ''}`}
                      desc="Scan the QR, then upload your payment screenshot">
                      {payMethod === 'gcash' && (
                        <div className="space-y-2">
                          {(pays.options || []).includes('deposit50') && (
                            <div className="flex gap-2 flex-wrap">
                              <AmountPill testid="op-gcash-full" active={amountChoice === 'full'} onClick={() => setAmountChoice('full')}>Pay 100% · ₱{fee}</AmountPill>
                              <AmountPill testid="op-gcash-deposit" active={amountChoice === 'deposit50'} onClick={() => setAmountChoice('deposit50')}>50% deposit · ₱{Math.round(fee / 2)}</AmountPill>
                            </div>
                          )}
                          <img src={pays.gcash_qr_url} alt="GCash QR" style={{ width: 150, height: 150, objectFit: 'contain', background: '#fff', borderRadius: 8, padding: 6 }} />
                          <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold cursor-pointer"
                            style={{ background: proofKey ? 'rgba(34,197,94,0.12)' : '#1A1A1A', color: proofKey ? '#22C55E' : '#A3A3A3', border: '1px solid #2A2A2A' }}>
                            <Upload className="h-4 w-4" />
                            {uploading ? 'Uploading…' : proofKey ? '✓ Proof uploaded — change' : 'Upload payment screenshot *'}
                            <input data-testid="op-proof-upload" type="file" accept="image/*" className="hidden"
                              onChange={e => uploadProof(e.target.files?.[0])} />
                          </label>
                        </div>
                      )}
                    </PayCard>
                  )}
                  {canVenue && (
                    <PayCard testid="op-pay-venue" active={payMethod === 'venue'} onClick={() => setPayMethod('venue')}
                      icon={Store} title="Pay at the venue" desc="Settle your entry fee at the court" />
                  )}
                </div>

                {/* Clear "amount to pay NOW vs LATER" summary */}
                <div className="mt-4 rounded-lg p-3" data-testid="op-pay-summary"
                  style={{ background: 'rgba(255,26,62,0.07)', border: '1px solid rgba(255,26,62,0.35)' }}>
                  {(() => {
                    let payNow = 0; let payLater = 0; let label = '';
                    if (payMethod === 'venue') {
                      payLater = fee; label = 'Settle in full at the venue';
                    } else if (payMethod === 'paymongo' || payMethod === 'gcash') {
                      if (amountChoice === 'deposit50') {
                        payNow = Math.round(fee / 2); payLater = fee - payNow;
                        label = payMethod === 'paymongo' ? 'Pay 50% online now · balance at venue' : 'Send 50% via GCash · balance at venue';
                      } else {
                        payNow = fee; payLater = 0;
                        label = payMethod === 'paymongo' ? 'Pay in full online now' : 'Send full amount via GCash';
                      }
                    }
                    return (
                      <>
                        <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: EYEBROW }}>You will pay</p>
                        {payNow > 0 && (
                          <p className="text-lg font-black" style={{ color: '#fff', fontFamily: 'Oswald, sans-serif' }}>
                            ₱{payNow.toLocaleString()} <span className="text-xs" style={{ color: '#A3A3A3' }}>now</span>
                          </p>
                        )}
                        {payLater > 0 && (
                          <p className="text-sm font-bold" style={{ color: '#A3A3A3' }}>
                            + ₱{payLater.toLocaleString()} at the venue
                          </p>
                        )}
                        {payNow === 0 && payLater === 0 && (
                          <p className="text-sm font-bold" style={{ color: '#22C55E' }}>Free entry</p>
                        )}
                        <p className="text-[11px] mt-1" style={{ color: '#737373' }}>{label}</p>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {error && <p className="text-xs mt-3" style={{ color: '#EF4444' }}>{error}</p>}
            <button data-testid="op-reg-submit" onClick={submit} disabled={saving || uploading}
              className="mt-5 w-full py-3.5 text-sm font-black uppercase"
              style={{ background: GRAD, color: '#fff', border: 'none', borderRadius: 999, boxShadow: '0 0 24px rgba(255,26,62,0.4)', letterSpacing: 1.5, opacity: (saving || uploading) ? 0.6 : 1, fontFamily: 'Oswald, sans-serif', cursor: 'pointer' }}>
              {saving ? 'Registering…'
                : joinsWaitlist ? 'Join the waitlist'
                : fee <= 0 ? 'Register — free entry'
                : payMethod === 'paymongo' ? `Register & pay ₱${(canDeposit && amountChoice === 'deposit50' ? Math.round(fee / 2) : fee).toLocaleString()} online`
                : payMethod === 'gcash' ? `Register · ₱${((pays.options || []).includes('deposit50') && amountChoice === 'deposit50' ? Math.round(fee / 2) : fee).toLocaleString()} via GCash`
                : `Register · pay ₱${fee.toLocaleString()} at the venue`}
            </button>
            </>
            )}
            <a href={`/book/${slug}/open-play/${sid}`} className="block text-center text-xs mt-3" style={{ color: '#8A8A82', textDecoration: 'none' }}>
              View live session →
            </a>
          </>
        )}
        </div>
      </div>
    </div>
  );
}

// Level outside the chosen division's range → a friendly pointer, never a block.
function DivisionAdvice({ divs, divisionId, level, onSwitch, testid }) {
  const div = (divs || []).find(d => d.id === divisionId);
  const fit = divisionFit(div, level);
  if (!fit || fit === 'ok') return null;
  const better = fittingDivision(divs, level);
  const lv = Math.round(level);
  return (
    <div data-testid={testid} className="mt-2 px-3 py-2 rounded-lg text-[11.5px]"
      style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#F5C26B' }}>
      Your level {lv} is {fit === 'above' ? 'above' : 'below'} {div.name} ({div.min_level}–{div.max_level}).
      {better ? <> You&apos;d enjoy <b>{better.name}</b> more.</> : null}
      {' '}You can still join {div.name}.
      {better && (
        <button type="button" data-testid={`${testid}-switch`} onClick={() => onSwitch(better.id)}
          className="ml-2 underline font-bold" style={{ background: 'none', border: 'none', color: '#F59E0B', cursor: 'pointer', padding: 0 }}>
          Switch to {better.name}
        </button>
      )}
    </div>
  );
}
